import React, { useState, useEffect } from 'react';
import { itinerariesService } from '../../services';
import EditItineraryModal from './EditItineraryModal';
import SecurePaymentDetailsModal from '../Payments/SecurePaymentDetailsModal';
import ItineraryMessagesModal from './ItineraryMessagesModal';
import { formatTravelDateDisplay } from '../../utils/dateUtils';

// Voyager Pro reskin (Figma ItineraryManagementPage) - list+drawer pattern
// matching Leads/Groups/Quotes. itinerariesService.js is already fully
// unified (no independent branching needed), so this component is shared by
// both agency (/agent/itineraries) and independent (/independent/itineraries)
// agents unchanged - see memory entity "TPCRM Voyager Pro CRM Reskin
// Project" for the migration history that made this possible.
//
// EditItineraryModal.jsx / SupplierPayments.jsx / ClientPayments.jsx were
// deliberately left un-reskinned for this pass (same scope boundary as
// Quote Builder was for Quotes Management) - only the list/management page
// itself was reskinned here.

const STATUS_CFG = {
  upcoming: { label: 'Upcoming', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
  active: { label: 'Traveling', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  completed: { label: 'Completed', color: 'text-slate-500', bg: 'bg-slate-100', border: 'border-slate-200', dot: 'bg-slate-400' },
};

const fmtMoney = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;

const getStatus = (itinerary) => {
  if (!itinerary.travel_start_date || !itinerary.travel_end_date) return 'upcoming';
  const now = new Date();
  const start = new Date(itinerary.travel_start_date);
  const end = new Date(itinerary.travel_end_date);
  if (now < start) return 'upcoming';
  if (now > end) return 'completed';
  return 'active';
};

const getClientName = (itinerary) => {
  if (itinerary.first_name && itinerary.last_name) return `${itinerary.first_name} ${itinerary.last_name}`;
  if (itinerary.client_name) return itinerary.client_name;
  if (itinerary.lead_traveler) {
    try {
      const lt = typeof itinerary.lead_traveler === 'string' ? JSON.parse(itinerary.lead_traveler) : itinerary.lead_traveler;
      const name = `${lt.firstName || lt.first_name || ''} ${lt.lastName || lt.last_name || ''}`.trim();
      if (name) return name;
    } catch (e) { /* ignore */ }
  }
  return 'Unknown Client';
};

// ─── Itinerary card ────────────────────────────────────────────────────────

function ItineraryCard({ itinerary, onOpen }) {
  const status = getStatus(itinerary);
  const cfg = STATUS_CFG[status];
  return (
    <button onClick={() => onOpen(itinerary)} className="w-full text-left bg-white border border-[var(--border)] rounded-xl p-4 hover:border-[var(--accent)] transition-colors flex items-center gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm truncate text-[var(--foreground)]">{itinerary.title}</span>
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </span>
        </div>
        <div className="text-xs text-[var(--muted-foreground)] mt-1 truncate">
          {getClientName(itinerary)} · {formatTravelDateDisplay(itinerary.travel_start_date)} – {formatTravelDateDisplay(itinerary.travel_end_date)}
        </div>
      </div>
      <div className="text-sm font-bold text-[var(--foreground)] flex-shrink-0">{fmtMoney(itinerary.total_amount)}</div>
    </button>
  );
}

// ─── Detail drawer ──────────────────────────────────────────────────────────

function ItineraryDetailDrawer({
  itinerary, isArchived, onClose,
  onEdit, onMessages, onSendUpdate, onSendPortalLink, onRecordPayment,
  onViewPayments, onArchive, onUnarchive, onDelete,
}) {
  const status = getStatus(itinerary);
  const cfg = STATUS_CFG[status];
  return (
    <div className="flex flex-col h-full bg-white border-l border-[var(--border)] overflow-hidden w-full sm:w-[420px] flex-shrink-0">
      <div className="flex-shrink-0 p-5 border-b border-[var(--border)]">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)] leading-tight truncate">{itinerary.title}</div>
            <div className="text-xs text-[var(--muted-foreground)] mt-0.5 truncate">{getClientName(itinerary)}</div>
          </div>
          <button onClick={onClose} className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] flex-shrink-0 p-1.5 rounded-lg hover:bg-[var(--muted)]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
          {cfg.label}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div>
          <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Trip Details</h3>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Travel Dates</span><span className="font-medium text-[var(--foreground)]">{formatTravelDateDisplay(itinerary.travel_start_date)} – {formatTravelDateDisplay(itinerary.travel_end_date)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Total</span><span className="font-semibold text-[var(--foreground)]">{fmtMoney(itinerary.total_amount)}</span></div>
            {itinerary.payment_status && (
              <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Payment Status</span><span className="font-medium text-[var(--foreground)] capitalize">{itinerary.payment_status.replace(/_/g, ' ')}</span></div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 p-4 border-t border-[var(--border)] space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => onEdit(itinerary)} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">✏️ Edit</button>
          <button onClick={() => onMessages(itinerary)} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">💬 Messages</button>
          <button onClick={() => onSendUpdate(itinerary)} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">📧 Send Update</button>
          <button
            onClick={() => onSendPortalLink(itinerary)}
            disabled={!itinerary.quote_id}
            title={itinerary.quote_id ? 'Send trip portal link to client' : 'Only available for quote-based itineraries'}
            className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            🔗 Portal Link
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => onRecordPayment(itinerary)} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">💰 Record Payment</button>
          <button onClick={() => onViewPayments(itinerary)} className="px-3 py-2.5 rounded-xl text-xs font-semibold border border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">🧾 View Payments</button>
        </div>
        {isArchived ? (
          <button onClick={() => onUnarchive(itinerary)} title="Move back to Active Bookings" className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all" style={{ backgroundColor: 'var(--primary)' }}>Unarchive</button>
        ) : (
          <button onClick={() => onArchive(itinerary)} title="Move to Completed Travel tab" className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all" style={{ backgroundColor: 'var(--accent)' }}>Archive</button>
        )}
        <button onClick={() => onDelete(itinerary)} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition-colors">Delete Itinerary</button>
      </div>
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

const ItinerariesList = () => {
  const [itineraries, setItineraries] = useState([]);
  const [completedItineraries, setCompletedItineraries] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [selectedItinerary, setSelectedItinerary] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItinerary, setEditingItinerary] = useState(null);
  const [initialTab, setInitialTab] = useState('details');

  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [itineraryForMessages, setItineraryForMessages] = useState(null);

  const [showSecurePaymentModal, setShowSecurePaymentModal] = useState(false);
  const [itineraryForPayments, setItineraryForPayments] = useState(null);

  const [showSendUpdateModal, setShowSendUpdateModal] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [sendingUpdate, setSendingUpdate] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itineraryForDeletion, setItineraryForDeletion] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => { fetchItineraries(); }, []);

  const fetchItineraries = async () => {
    try {
      setLoading(true);
      const [activeData, completedData] = await Promise.all([
        itinerariesService.getAll(),
        itinerariesService.getCompleted(),
      ]);
      setItineraries(Array.isArray(activeData) ? activeData : activeData.data || []);
      setCompletedItineraries(Array.isArray(completedData) ? completedData : completedData.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching itineraries:', err);
      setError('Failed to load itineraries');
    } finally {
      setLoading(false);
    }
  };

  const currentItineraries = activeTab === 'active' ? itineraries : completedItineraries;
  const filteredItineraries = currentItineraries.filter(itinerary => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term || itinerary.title?.toLowerCase().includes(term) || getClientName(itinerary).toLowerCase().includes(term);
    const matchesStatus = statusFilter === 'all' || getStatus(itinerary) === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // ── Actions ────────────────────────────────────────────────────────────
  const openItinerary = (itinerary) => setSelectedItinerary(itinerary);
  const closeDrawer = () => setSelectedItinerary(null);

  const handleEdit = async (itinerary, tab = 'details') => {
    try {
      const fullItinerary = await itinerariesService.getById(itinerary.id);
      setEditingItinerary(fullItinerary);
      setInitialTab(tab);
      setShowEditModal(true);
    } catch (err) {
      console.error('Error fetching itinerary details:', err);
      alert('Failed to load itinerary details');
    }
  };

  const handleRecordPayment = (itinerary) => handleEdit(itinerary, 'supplier-payments');

  const handleMessages = (itinerary) => { setItineraryForMessages(itinerary); setShowMessagesModal(true); };
  const closeMessagesModal = () => { setShowMessagesModal(false); setItineraryForMessages(null); };

  const handleSendUpdate = (itinerary) => { setSelectedItinerary(itinerary); setUpdateMessage(''); setShowSendUpdateModal(true); };

  const handleSendUpdateSubmit = async () => {
    if (!updateMessage.trim()) { alert('Please enter a message about the update'); return; }
    setSendingUpdate(true);
    try {
      await itinerariesService.sendUpdate(selectedItinerary.id, { message: updateMessage });
      alert('Update sent to client successfully!');
      setShowSendUpdateModal(false);
      setUpdateMessage('');
      fetchItineraries();
    } catch (err) {
      console.error('Error sending update:', err);
      alert('Failed to send update. Please try again.');
    } finally {
      setSendingUpdate(false);
    }
  };

  const handleSendPortalLink = async (itinerary) => {
    if (!itinerary.quote_id) {
      alert('This itinerary is not linked to a quote. Portal links are only available for quote-based itineraries.');
      return;
    }
    if (!window.confirm(`Send trip portal link to ${getClientName(itinerary)}?`)) return;
    try {
      const response = await fetch(`/api/public/portal/send/${itinerary.quote_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Failed to send portal link');
      }
      alert(`Portal link sent successfully to ${getClientName(itinerary)}!`);
    } catch (err) {
      console.error('Error sending portal link:', err);
      alert(`Failed to send portal link: ${err.message}`);
    }
  };

  const handleViewPayments = (itinerary) => { setItineraryForPayments(itinerary); setShowSecurePaymentModal(true); };

  const handleDeleteClick = (itinerary) => { setItineraryForDeletion(itinerary); setDeleteConfirmText(''); setShowDeleteModal(true); };
  const closeDeleteModal = () => { setShowDeleteModal(false); setItineraryForDeletion(null); setDeleteConfirmText(''); };

  const handleDeleteConfirm = async () => {
    if (!itineraryForDeletion) return;
    if (deleteConfirmText !== 'DELETE') { alert('Please type "DELETE" to confirm deletion'); return; }
    try {
      setIsDeleting(true);
      await itinerariesService.delete(itineraryForDeletion.id);
      setItineraries(prev => prev.filter(i => i.id !== itineraryForDeletion.id));
      setCompletedItineraries(prev => prev.filter(i => i.id !== itineraryForDeletion.id));
      setSelectedItinerary(null);
      closeDeleteModal();
    } catch (err) {
      console.error('Error deleting itinerary:', err);
      alert('Failed to delete itinerary. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleArchive = async (itinerary) => {
    if (!window.confirm(`Archive "${itinerary.title}"? This will move it to the Completed Travel tab.`)) return;
    try {
      await itinerariesService.archive(itinerary.id);
      setItineraries(prev => prev.filter(i => i.id !== itinerary.id));
      setCompletedItineraries(prev => [itinerary, ...prev]);
      setSelectedItinerary(null);
    } catch (err) {
      console.error('Error archiving itinerary:', err);
      alert('Failed to archive itinerary. Please try again.');
    }
  };

  const handleUnarchive = async (itinerary) => {
    if (!window.confirm(`Unarchive "${itinerary.title}"? This will move it back to Active Bookings.`)) return;
    try {
      await itinerariesService.unarchive(itinerary.id);
      setCompletedItineraries(prev => prev.filter(i => i.id !== itinerary.id));
      setItineraries(prev => [itinerary, ...prev]);
      setSelectedItinerary(null);
    } catch (err) {
      console.error('Error unarchiving itinerary:', err);
      alert('Failed to unarchive itinerary. Please try again.');
    }
  };

  if (loading) {
    return <div className="voyager-app flex items-center justify-center min-h-[400px] text-sm text-[var(--muted-foreground)]">Loading itineraries…</div>;
  }

  return (
    <div className="voyager-app flex h-full min-h-0">
      <div className={`flex-1 overflow-y-auto min-w-0 ${selectedItinerary ? 'hidden lg:block' : ''}`}>
        <div className="max-w-5xl mx-auto p-6 space-y-6">
          <div>
            <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-1">Itineraries</h1>
            <p className="text-[var(--muted-foreground)] text-sm">Manage your clients' travel bookings, payments, and updates.</p>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white border border-[var(--border)] rounded-xl p-4">
              <div className="text-2xl font-bold text-[var(--primary)]">{itineraries.length}</div>
              <div className="text-xs text-[var(--muted-foreground)] mt-0.5">Active Bookings</div>
            </div>
            <div className="bg-white border border-[var(--border)] rounded-xl p-4">
              <div className="text-2xl font-bold text-[var(--muted-foreground)]">{completedItineraries.length}</div>
              <div className="text-xs text-[var(--muted-foreground)] mt-0.5">Completed Travel</div>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${activeTab === 'active' ? 'text-white' : 'text-[var(--muted-foreground)] hover:bg-[var(--muted)]'}`}
              style={activeTab === 'active' ? { backgroundColor: 'var(--primary)' } : {}}
            >
              Active Bookings ({itineraries.length})
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${activeTab === 'completed' ? 'text-white' : 'text-[var(--muted-foreground)] hover:bg-[var(--muted)]'}`}
              style={activeTab === 'completed' ? { backgroundColor: 'var(--primary)' } : {}}
            >
              Completed Travel ({completedItineraries.length})
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input
                type="text"
                placeholder="Search by title or client name…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors"
              />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none cursor-pointer flex-shrink-0">
              <option value="all">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="active">Currently Traveling</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {filteredItineraries.length === 0 ? (
            <div className="text-center py-16 bg-white border-2 border-dashed border-[var(--border)] rounded-2xl">
              <div className="text-4xl mb-3">🧳</div>
              <div className="text-sm font-medium text-[var(--foreground)] mb-1">No itineraries found</div>
              <div className="text-xs text-[var(--muted-foreground)]">Convert a quote to create your first itinerary.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredItineraries.map(itinerary => (
                <ItineraryCard key={itinerary.id} itinerary={itinerary} onOpen={openItinerary} />
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedItinerary && !showSendUpdateModal && (
        <ItineraryDetailDrawer
          itinerary={selectedItinerary}
          isArchived={activeTab === 'completed'}
          onClose={closeDrawer}
          onEdit={handleEdit}
          onMessages={handleMessages}
          onSendUpdate={handleSendUpdate}
          onSendPortalLink={handleSendPortalLink}
          onRecordPayment={handleRecordPayment}
          onViewPayments={handleViewPayments}
          onArchive={handleArchive}
          onUnarchive={handleUnarchive}
          onDelete={handleDeleteClick}
        />
      )}

      <EditItineraryModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingItinerary(null); setInitialTab('details'); }}
        itinerary={editingItinerary}
        initialTab={initialTab}
        onUpdate={() => { fetchItineraries(); setShowEditModal(false); setEditingItinerary(null); setInitialTab('details'); }}
      />

      {showMessagesModal && itineraryForMessages && (
        <ItineraryMessagesModal isOpen={showMessagesModal} itinerary={itineraryForMessages} onClose={closeMessagesModal} />
      )}

      <SecurePaymentDetailsModal
        isOpen={showSecurePaymentModal}
        onClose={() => { setShowSecurePaymentModal(false); setItineraryForPayments(null); }}
        itinerary={itineraryForPayments}
      />

      {/* Send Update Modal */}
      {showSendUpdateModal && selectedItinerary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowSendUpdateModal(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)]">
              <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">Send Update to Client</h2>
              <button onClick={() => setShowSendUpdateModal(false)} className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-[var(--muted-foreground)] mb-4">Describe the changes you've made to the itinerary. This message will be sent to the client.</p>
              <label className="block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider mb-1.5">Update Message *</label>
              <textarea
                value={updateMessage}
                onChange={e => setUpdateMessage(e.target.value)}
                rows={4}
                placeholder="e.g., Updated your hotel to a suite with ocean view…"
                className="w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors resize-y"
              />
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--border)]">
              <button onClick={() => setShowSendUpdateModal(false)} className="px-5 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">Cancel</button>
              <button
                onClick={handleSendUpdateSubmit}
                disabled={sendingUpdate || !updateMessage.trim()}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all disabled:opacity-50"
                style={{ backgroundColor: 'var(--primary)' }}
              >
                {sendingUpdate ? 'Sending…' : 'Send Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itineraryForDeletion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={closeDeleteModal}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)]">
              <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">⚠️ Delete Itinerary</h2>
              <button onClick={closeDeleteModal} className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-3 max-h-[60vh] overflow-y-auto">
              <div className="p-3.5 bg-red-50 border-2 border-red-200 rounded-xl">
                <p className="text-sm font-bold text-red-700 mb-1">⚠️ WARNING: This action cannot be undone!</p>
                <p className="text-sm text-[var(--muted-foreground)]">You are about to permanently delete this itinerary and all associated data.</p>
              </div>

              <div className="p-3.5 bg-[var(--muted)]/40 rounded-xl text-sm space-y-1">
                <div><strong className="text-[var(--foreground)]">Title:</strong> {itineraryForDeletion.title}</div>
                <div><strong className="text-[var(--foreground)]">Client:</strong> {getClientName(itineraryForDeletion)}</div>
                <div><strong className="text-[var(--foreground)]">Travel Dates:</strong> {formatTravelDateDisplay(itineraryForDeletion.travel_start_date)} – {formatTravelDateDisplay(itineraryForDeletion.travel_end_date)}</div>
                <div><strong className="text-[var(--foreground)]">Total Amount:</strong> {fmtMoney(itineraryForDeletion.total_amount)}</div>
              </div>

              {getStatus(itineraryForDeletion) === 'active' && (
                <div className="p-3.5 bg-red-50 border-2 border-red-200 rounded-xl">
                  <p className="text-sm font-bold text-red-700 mb-1">🚨 CRITICAL: This client is currently traveling!</p>
                  <p className="text-sm text-[var(--muted-foreground)]">Deleting this itinerary may disrupt their active trip and cause confusion.</p>
                </div>
              )}
              {getStatus(itineraryForDeletion) === 'upcoming' && (
                <div className="p-3.5 bg-amber-50 border-2 border-amber-200 rounded-xl">
                  <p className="text-sm font-bold text-amber-800 mb-1">⚠️ This is an upcoming trip.</p>
                  <p className="text-sm text-[var(--muted-foreground)]">Deleting this itinerary may affect the client's travel plans.</p>
                </div>
              )}
              {itineraryForDeletion.hasPayments && (
                <div className="p-3.5 bg-red-50 border-2 border-red-200 rounded-xl">
                  <p className="text-sm font-bold text-red-700 mb-1">🚨 This itinerary has {itineraryForDeletion.payment_count} payment(s) associated with it!</p>
                  <p className="text-sm text-[var(--muted-foreground)]">Deleting this itinerary may affect payment records and financial reporting.</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider mb-1.5">Type "DELETE" to confirm</label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="w-full px-3.5 py-2.5 text-sm border border-red-300 rounded-xl bg-white text-[var(--foreground)] focus:border-red-500 focus:outline-none transition-colors"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--border)]">
              <button onClick={closeDeleteModal} disabled={isDeleting} className="px-5 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors disabled:opacity-50">Cancel</button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-600 hover:brightness-105 transition-all disabled:opacity-50"
              >
                {isDeleting ? 'Deleting…' : 'Delete Itinerary Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItinerariesList;
